"use client";

import { useEffect, useMemo, useState } from "react";

import "@workspace/ui/styles/banner-strip.css";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";
import { GraphsIcon } from "@/components/ui/icons/grpahs";
import { useModal } from "@/components/providers/modal-provider";
import { useQuizState } from "@/components/quiz/hooks/use-quiz-state";
import { fireQuizTouchpointImpression } from "@/components/quiz/hooks/use-quiz-touchpoint-impression";
import { QUIZ_SOURCE } from "@/components/quiz/constants";
import { useApp } from "@/components/providers/app-provider";
import {
  FALLBACK_DISCOUNT_CODE_PRICE,
  STARTER_KIT_PRICE,
} from "@/lib/utils/common/constants";
import { getSaleComponent, isSaleEnabled, saleConfig } from "@/lib/config/sale";
import { UsFlagIcon } from "@/components/ui/icons/us-flag";

const ROTATE_MS = 3000;
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const DEFAULT_MOBILE_BACKGROUND = "var(--color-base-13)";
const DEFAULT_DESKTOP_BACKGROUND = "var(--color-base-12)";

export type StickyBannerBadge = {
  text: string;
  icon?: "sparkle" | "dot";
  background?: string;
};

export type StickyBannerCta = {
  text: string;
  href?: string;
  icon?: "arrow-small" | "arrow-long" | "chevron";
  style?: "inline" | "underline";
};

export type StickyBannerBenefitItem = {
  icon: "shipping" | "eligible" | "hormone-graphs";
  text: string;
};

export type StickyBannerModalContent = {
  logo?: "hormone-graphs";
  text: string;
  modalId: string;
  cta?: StickyBannerCta;
};

export type StickyBannerContent =
  | {
      type: "promo";
      eyebrow?: StickyBannerBadge;
      message: string;
      cta?: StickyBannerCta;
    }
  | {
      type: "benefits";
      items: StickyBannerBenefitItem[];
    }
  | {
      type: "sale";
      href?: string;
    }
  | {
      type: "quiz";
      items: StickyBannerModalContent;
      cta?: StickyBannerCta;
    }

export type StickyBannerBreakpoint = {
  background?: string;
  layout: "split" | "center";
  content: StickyBannerContent;
  showDesktopTexture?: boolean;
};

export type StickyBannerSlide = {
  id: string;
  mobile?: StickyBannerBreakpoint;
  desktop?: StickyBannerBreakpoint;
};

type StickyBannerProps = {
  slides: StickyBannerSlide[];
};

function LaunchSparkle() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="h-[15.5px] w-[15.5px]"
    >
      <path
        d="M5.8982 3.70386C6.90934 6.43641 9.06379 8.59086 11.7963 9.602C9.06379 10.6131 6.90934 12.7676 5.8982 15.5001C4.88706 12.7676 2.73262 10.6131 6.10352e-05 9.602C2.73261 8.59086 4.88706 6.43641 5.8982 3.70386Z"
        fill="#BF5AC8"
      />
      <path
        d="M12.3301 0C12.8735 1.46842 14.0312 2.62618 15.4996 3.16954C14.0312 3.7129 12.8735 4.87066 12.3301 6.33908C11.7867 4.87066 10.629 3.7129 9.16055 3.16954C10.629 2.62618 11.7867 1.46842 12.3301 0Z"
        fill="#FE9804"
      />
    </svg>
  );
}

function DotIndicator() {
  return (
    <span aria-hidden="true" className="size-2 rounded-full bg-[#16CF5E]" />
  );
}

function ArrowRightSmall() {
  return (
    <svg
      aria-hidden="true"
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      className="h-[22px] w-[22px] shrink-0"
    >
      <path
        d="M8.5 4.6L14.9 11L8.5 17.4"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
    </svg>
  );
}

function ArrowRightLong() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="h-5 w-5 shrink-0"
    >
      <path
        d="M17 10H3M17 10L12.5 5.5M17 10L12.5 14.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
    </svg>
  );
}

function ArrowRightChevron() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="h-5 w-5 shrink-0"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.73298 5.73123C5.04363 5.42292 5.54728 5.42292 5.85793 5.73123L10.1003 9.94176C10.411 10.2501 10.411 10.7499 10.1003 11.0582L5.85793 15.2688C5.54728 15.5771 5.04363 15.5771 4.73298 15.2688C4.42234 14.9605 4.42234 14.4606 4.73298 14.1523L8.41294 10.5L4.73298 6.84772C4.42234 6.53941 4.42234 6.03954 4.73298 5.73123Z"
        fill="white"
      />
    </svg>
  );
}

function DesktopBannerTexture() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="55"
      height="48"
      viewBox="0 0 55 48"
      fill="none"
      className="home-banner-texture-sweep pointer-events-none absolute left-0 top-0 h-12 w-[55px]"
    >
      <path d="M30 0H46L16 48H0L30 0Z" fill="#D9D9D9" fillOpacity="0.19" />
      <path d="M49 0H55L25 48H19L49 0Z" fill="#D9D9D9" fillOpacity="0.19" />
    </svg>
  );
}

function FreeShippingIcon() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="h-5 w-5 shrink-0"
    >
      <path
        d="M17.0502 5.47508H17.6752V5.10234L17.3472 4.92518L17.0502 5.47508ZM10.0002 1.66675L10.2972 1.11685L10.0002 0.956388L9.70315 1.11685L10.0002 1.66675ZM2.9502 5.47508L2.65315 4.92518L2.3252 5.10234V5.47508H2.9502ZM17.0502 14.5251L17.3472 15.075L17.6752 14.8978V14.5251H17.0502ZM10.0002 18.3334L9.70315 18.8833L10.0002 19.0438L10.2972 18.8833L10.0002 18.3334ZM2.9502 14.5251H2.3252V14.8978L2.65315 15.075L2.9502 14.5251ZM13.5212 3.99124C13.8201 3.81855 13.9224 3.43627 13.7497 3.13739C13.577 2.83852 13.1947 2.73623 12.8958 2.90892L13.2085 3.45008L13.5212 3.99124ZM6.05341 6.76251C5.74986 6.92685 5.63701 7.30614 5.80134 7.60969C5.96567 7.91324 6.34497 8.0261 6.64851 7.86176L6.35096 7.31214L6.05341 6.76251ZM17.0502 5.47508L17.3472 4.92518L10.2972 1.11685L10.0002 1.66675L9.70315 2.21665L16.7531 6.02498L17.0502 5.47508ZM10.0002 1.66675C9.70315 1.11685 9.70313 1.11686 9.7031 1.11688C9.70306 1.11689 9.70302 1.11692 9.70295 1.11696C9.70281 1.11703 9.70263 1.11713 9.70236 1.11727C9.70184 1.11755 9.70106 1.11798 9.70002 1.11854C9.69795 1.11966 9.69484 1.12134 9.69074 1.12355C9.68252 1.12799 9.67029 1.1346 9.65422 1.14328C9.62208 1.16064 9.57457 1.1863 9.51314 1.21949C9.39027 1.28586 9.21167 1.38234 8.98875 1.50276C8.54291 1.7436 7.91977 2.08021 7.2106 2.4633C5.79224 3.22948 4.02974 4.18156 2.65315 4.92518L2.9502 5.47508L3.24724 6.02498C4.62384 5.28136 6.38634 4.32927 7.80469 3.56309C8.51387 3.18001 9.13701 2.84339 9.58285 2.60255C9.80577 2.48213 9.98437 2.38566 10.1072 2.31928C10.1687 2.2861 10.2162 2.26044 10.2483 2.24308C10.2644 2.23439 10.2766 2.22779 10.2848 2.22335C10.2889 2.22113 10.292 2.21945 10.2941 2.21833C10.2951 2.21778 10.296 2.21734 10.2965 2.21707C10.2967 2.21694 10.2969 2.21681 10.297 2.21675C10.2971 2.21672 10.2972 2.21669 10.2972 2.21667C10.2972 2.21665 10.2972 2.21665 10.0002 1.66675ZM10.0002 9.28341L10.2972 9.83331L17.3472 6.02498L17.0502 5.47508L16.7531 4.92518L9.70315 8.73352L10.0002 9.28341ZM17.0502 5.47508H16.4252V14.5251H17.0502H17.6752V5.47508H17.0502ZM17.0502 14.5251L16.7531 13.9752L9.70315 17.7835L10.0002 18.3334L10.2972 18.8833L17.3472 15.075L17.0502 14.5251ZM10.0002 18.3334H10.6252V9.28341H10.0002H9.3752V18.3334H10.0002ZM10.0002 9.28341C10.2972 8.73352 10.2972 8.73351 10.2972 8.73349C10.2972 8.73347 10.2971 8.73345 10.297 8.73341C10.297 8.73338 10.2967 8.7332 10.2965 8.73309C10.296 8.73283 10.2951 8.73237 10.2941 8.73183C10.292 8.73071 10.2889 8.72903 10.2848 8.72681C10.2766 8.72237 10.2644 8.71577 10.2483 8.70709C10.2162 8.68972 10.1687 8.66406 10.1072 8.63088C9.98437 8.56451 9.80577 8.46803 9.58285 8.34761C9.13701 8.10677 8.51387 7.77016 7.80469 7.38707C6.38634 6.62089 4.62384 5.66881 3.24724 4.92518L2.9502 5.47508L2.65315 6.02498C4.02974 6.7686 5.79224 7.72069 7.2106 8.48686C7.91977 8.86995 8.54291 9.20656 8.98875 9.4474C9.21168 9.56783 9.39027 9.6643 9.51314 9.73067C9.57456 9.76385 9.62209 9.78953 9.65422 9.80688C9.67028 9.81556 9.68253 9.82218 9.69074 9.82661C9.69487 9.82884 9.69792 9.83049 9.70002 9.83162C9.70108 9.8322 9.70182 9.83259 9.70236 9.83289C9.7025 9.83296 9.70295 9.8332 9.70295 9.83321C9.70302 9.83324 9.70306 9.83327 9.7031 9.83329C9.70313 9.8333 9.70315 9.83331 10.0002 9.28341ZM2.9502 5.47508H2.3252V14.5251H2.9502H3.5752V5.47508H2.9502ZM2.9502 14.5251L2.65315 15.075L9.70315 18.8833L10.0002 18.3334L10.2972 17.7835L3.24724 13.9752L2.9502 14.5251ZM13.2085 3.45008C12.8958 2.90892 12.8958 2.90893 12.8958 2.90895C12.8958 2.90896 12.8957 2.90899 12.8957 2.90902C12.8956 2.90909 12.8954 2.90919 12.8951 2.90933C12.8946 2.90961 12.8939 2.91004 12.8929 2.9106C12.891 2.91173 12.888 2.91343 12.8841 2.91568C12.8763 2.92018 12.8647 2.9269 12.8494 2.93573C12.8188 2.9534 12.7735 2.97954 12.7148 3.01335C12.5974 3.08096 12.4266 3.17927 12.213 3.30191C11.7858 3.5472 11.1875 3.88979 10.5032 4.27887C9.13381 5.05762 7.42405 6.02049 6.05341 6.76251L6.35096 7.31214L6.64851 7.86176C8.03108 7.11329 9.7501 6.14513 11.1211 5.36547C11.8072 4.97535 12.407 4.63189 12.8354 4.38594C13.0496 4.26296 13.221 4.16434 13.3388 4.09644C13.3977 4.06249 13.4433 4.03622 13.4741 4.01842C13.4896 4.00952 13.5013 4.00274 13.5092 3.99818C13.5132 3.9959 13.5161 3.99417 13.5182 3.99301C13.5192 3.99243 13.5199 3.99199 13.5204 3.99169C13.5207 3.99155 13.5209 3.99143 13.521 3.99136C13.5211 3.99132 13.5211 3.99129 13.5212 3.99127C13.5212 3.99125 13.5212 3.99124 13.2085 3.45008Z"
        fill="#F3F2F1"
      />
    </svg>
  );
}

function HsaIcon() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="h-5 w-5 shrink-0"
    >
      <path
        d="M4.80078 8.76997L8.91222 12.8002L17.6008 3.2002"
        stroke="#F3F2F1"
        strokeWidth="1.25"
        strokeMiterlimit="10"
      />
      <path
        d="M16.5839 6.41265C17.1339 7.48765 17.4422 8.70848 17.4422 10.0001C17.4422 14.371 13.9005 17.9126 9.52969 17.9126C5.15886 17.9126 1.61719 14.371 1.61719 10.0001C1.61719 5.62931 5.15886 2.08765 9.52969 2.08765C11.5839 2.08765 13.4547 2.87098 14.8589 4.15015"
        stroke="#F3F2F1"
        strokeWidth="1.25"
        strokeMiterlimit="10"
      />
    </svg>
  );
}

// ─── Sale strip components ────────────────────────────────────────────────────


function SaleSlideContent({
  layout,
  idBase,
}: {
  layout: "split" | "center";
  idBase: string;
}) {
  const sale = getSaleComponent("stickyBanner");
  const price = STARTER_KIT_PRICE["ng"] - FALLBACK_DISCOUNT_CODE_PRICE["ng"];
  const badgeText = sale?.badgeText ?? "Flash Sale is Live";
  const message = (sale?.message ?? "Get the most precise Inito at ${price}").replace(
    "${price}",
    `$${price}`,
  );
  const ctaText = sale?.ctaText ?? "Know more";

  const badge = (
    <span
      className="flex shrink-0 items-center gap-1 rounded-[6px] px-[7px] py-[6px] text-white"
      style={{
        background:
          "linear-gradient(123deg, rgba(43, 112, 240, 1) 2%, rgba(230, 53, 53, 1) 100%)",
      }}
    >
      {sale?.showFlag ? <UsFlagIcon /> : null}
      <span
        id={`${idBase}-sale-badge-text`}
        data-testid={`${idBase}-sale-badge-text`}
        className="sale-badge-text font-medium leading-[16px]"
      >
        {badgeText}
      </span>
    </span>
  );

  if (layout === "split") {
    return (
      <div
        className="flex h-12 w-full items-center justify-between gap-1 px-4"
      >
        <div className="flex min-w-0 items-center gap-2">
          {badge}
          <span
            id={`${idBase}-sale-message`}
            data-testid={`${idBase}-sale-message`}
            className="sale-badge-message whitespace-nowrap font-medium tracking-[-0.3px] text-white"
          >
            {message}
          </span>
        </div>
        <ArrowRightChevron />
      </div>
    );
  }

  return (
    <div
      className="flex h-12 w-full items-center justify-center px-[60px]"
    >
      <div className="flex items-center gap-12">
        <div className="flex items-center gap-2">
          {badge}
          <span
            id={`${idBase}-sale-message`}
            data-testid={`${idBase}-sale-message`}
            className="text-[14px] font-normal leading-[1.1] tracking-[-0.01em] text-white"
          >
            {message}
          </span>
        </div>
        <span
          className="flex items-center"
        >
          <span
            id={`${idBase}-sale-cta-text`}
            data-testid={`${idBase}-sale-cta-text`}
            className="title-14 text-white"
          >
            {ctaText}
          </span>
          <ArrowRightChevron />
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function getResolvedBreakpoint(
  slide: StickyBannerSlide,
  isMobile: boolean,
): StickyBannerBreakpoint | null {
  return isMobile
    ? (slide.mobile ?? slide.desktop ?? null)
    : (slide.desktop ?? slide.mobile ?? null);
}

function renderBadge(
  badge: StickyBannerBadge | undefined,
  isMobile: boolean,
  idBase: string,
) {
  if (!badge) return null;

  return (
    <span
      className="flex items-center gap-[4px] rounded-[6px] text-white"
      style={{
        padding: "0 7px",
        background:
          badge.background ??
          "linear-gradient(90deg, #510B8F 0%, #C48828 145.13%)",
      }}
    >
      {badge.icon === "sparkle" ? <LaunchSparkle /> : null}
      {badge.icon === "dot" ? <DotIndicator /> : null}
      <span
        id={`${idBase}-badge-text`}
        data-testid={`${idBase}-badge-text`}
        className="title-12 font-medium leading-[22px] tracking-[-0.4px]"
      >
        {badge.text}
      </span>
    </span>
  );
}

function renderCtaIcon(icon: StickyBannerCta["icon"] | undefined) {
  if (icon === "arrow-long") return <ArrowRightLong />;
  if (icon === "chevron") return <ArrowRightChevron />;
  return <ArrowRightSmall />;
}

function getSlideHref(
  slide: StickyBannerSlide,
  isMobile: boolean,
): string | null {
  const breakpoint = getResolvedBreakpoint(slide, isMobile);
  if (!breakpoint) return null;
  if (breakpoint.content.type === "promo") return breakpoint.content.cta?.href ?? null;
  if (breakpoint.content.type === "sale") return breakpoint.content.href ?? null;
  return null;
}

function getSlideModalId(
  slide: StickyBannerSlide,
  isMobile: boolean,
): string | null {
  const breakpoint = getResolvedBreakpoint(slide, isMobile);
  if (!breakpoint || breakpoint.content.type !== "quiz") return null;
  return breakpoint.content.items?.modalId ?? null;
}

// Where to scroll when a target section isn't on the page: the region experiment swaps
// the feature carousel (#product-tech) for tech stories (#tech-stories).
const SECTION_ID_FALLBACKS: Record<string, string> = {
  "product-tech": "tech-stories",
};

function getSectionIdFromHref(href: string): string | null {
  if (href.startsWith("/#")) return href.slice(2);
  if (href.startsWith("#")) return href.slice(1);
  return null;
}

function getVisibleSectionById(sectionId: string): HTMLElement | null {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(`[id="${sectionId}"]`),
  );

  return (
    candidates.find((candidate) => {
      if (candidate.getClientRects().length === 0) return false;
      const style = window.getComputedStyle(candidate);
      return style.display !== "none" && style.visibility !== "hidden";
    }) ?? candidates[0] ?? null
  );
}

function renderPromoContent(
  content: Extract<StickyBannerContent, { type: "promo" }>,
  layout: StickyBannerBreakpoint["layout"],
  isMobile: boolean,
  idBase: string,
) {
  const hideInlineText =
    isMobile && layout === "split" && content.cta?.style !== "underline";

  if (layout === "split") {
    return (
      <div
        className="flex h-12 w-full items-center justify-between gap-1 px-4"
      >
        <div className="flex min-w-0 items-center gap-2">
          {renderBadge(content.eyebrow, true, `${idBase}-promo`)}
          <span
            id={`${idBase}-promo-message`}
            data-testid={`${idBase}-promo-message`}
            className="title-14 truncate text-primary-foreground"
          >
            {content.message}
          </span>
        </div>

        {content.cta ? (
          <span
            aria-label={content.cta.text}
            className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center"
          >
            {hideInlineText ? (
              <span
                id={`${idBase}-promo-cta-label`}
                data-testid={`${idBase}-promo-cta-label`}
                className="sr-only"
              >{content.cta.text}</span>
            ) : null}
            {renderCtaIcon(content.cta.icon)}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex h-12 w-full items-center justify-center px-[60px]"
    >
      <div className="flex items-center gap-8">
        <div className="flex w-[337.5px] items-center gap-3">
          {renderBadge(content.eyebrow, false, `${idBase}-promo`)}
          <span
            id={`${idBase}-promo-message`}
            data-testid={`${idBase}-promo-message`}
            className="title-16 text-primary-foreground"
          >
            {content.message}
          </span>
        </div>

        {content.cta ? (
          <span
            className="flex items-center"
          >
            <span
              id={`${idBase}-promo-cta-text`}
              data-testid={`${idBase}-promo-cta-text`}
              className="title-14 text-primary-foreground"
            >
              {content.cta.text}
            </span>
            {renderCtaIcon(content.cta.icon)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function renderBenefitIcon(icon: StickyBannerBenefitItem["icon"]) {
  if (icon === "shipping") return <FreeShippingIcon />;
  return <HsaIcon />;
}

function renderBenefitsContent(
  content: Extract<StickyBannerContent, { type: "benefits" }>,
  isMobile: boolean,
  idBase: string,
) {
  return (
    <div
      className={
        isMobile
          ? "flex h-12 w-full items-center px-4"
          : "flex h-12 w-full items-center justify-center px-4"
      }
    >
      <div
        className={
          isMobile
            ? "flex w-full items-center justify-center gap-9"
            : "mx-auto flex w-full max-w-[430px] items-center justify-center gap-9"
        }
      >
        {content.items.map((item, index) => (
          <div
            key={`${item.icon}-${item.text}`}
            className="flex items-center gap-3"
          >
            {renderBenefitIcon(item.icon)}
            <span
              id={`${idBase}-benefit-${index + 1}-text`}
              data-testid={`${idBase}-benefit-${index + 1}-text`}
              className="title-14 text-light-olive"
            >{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderModalContent(content: Extract<StickyBannerContent, { type: "quiz" }>, layout: StickyBannerBreakpoint["layout"], ctaLabel?: string, idBase?: string) {

  const modalId = content.items?.modalId;
  const ctaText = ctaLabel ?? content.cta?.text;

  if (layout === "split") {
    return (
      <div
        className="flex h-12 w-full items-center justify-between gap-1 px-4"
      >
        <div className="flex min-w-0 items-center gap-2 w-full">
          {renderModalIcon(content.items?.logo)}
          <span
            id={`${idBase}-quiz-text`}
            data-testid={`${idBase}-quiz-text`}
            className="title-14 truncate text-primary-foreground flex-1"
          >{content.items?.text}</span>
          {modalId ? (
            <span
              className="flex shrink-0 items-center gap-1"
            >
              {ctaText ? <span
                id={`${idBase}-quiz-cta-text`}
                data-testid={`${idBase}-quiz-cta-text`}
                className="title-14 text-primary-foreground"
              >{ctaText}</span> : null}
              {renderCtaIcon(content.cta?.icon)}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-12 w-full items-center px-4"
    >
      <div className="flex w-full items-center justify-center">
        <div className="flex items-center justify-center gap-8">
          <div className="flex items-center justify-center gap-3">
            {renderModalIcon(content.items?.logo)}
            <span
              id={`${idBase}-quiz-text`}
              data-testid={`${idBase}-quiz-text`}
              className="title-14 text-primary-foreground flex-1"
            >{content.items?.text}</span>
          </div>
          {modalId && content.cta?.icon ? <span
            className="flex items-center"
          >
            <span
              id={`${idBase}-quiz-cta-text`}
              data-testid={`${idBase}-quiz-cta-text`}
              className="title-14 text-primary-foreground"
            >
              {ctaText}
            </span>
            {renderCtaIcon(content.cta?.icon)}
          </span> : null}
        </div>
      </div>
    </div>
  );
}



function renderModalIcon(logo: StickyBannerModalContent["logo"]) {
  if (logo === "hormone-graphs") return <GraphsIcon />;
  return null;
}

function getSlideBackground(slide: StickyBannerSlide, isMobile: boolean) {
  if (isSaleEnabled("stickyBanner")) return "#000000";
  const breakpoint = getResolvedBreakpoint(slide, isMobile);
  if (!breakpoint) return isMobile ? DEFAULT_MOBILE_BACKGROUND : DEFAULT_DESKTOP_BACKGROUND;
  return breakpoint.background ?? (isMobile ? DEFAULT_MOBILE_BACKGROUND : DEFAULT_DESKTOP_BACKGROUND);
}

function renderBreakpointContent(
  slide: StickyBannerSlide,
  isMobile: boolean,
  quizCtaLabel: string | undefined,
  index: number,
) {
  const breakpoint = getResolvedBreakpoint(slide, isMobile);
  if (!breakpoint) return null;

  const idBase = `sticky-banner-slide-${index + 1}-${isMobile ? "mobile" : "desktop"}`;

  if (breakpoint.content.type === "sale") {
    return <SaleSlideContent layout={breakpoint.layout} idBase={idBase} />;
  }

  return breakpoint.content.type === "promo"
    ? renderPromoContent(breakpoint.content, breakpoint.layout, isMobile, idBase)
    : breakpoint.content.type === "quiz"
    ? renderModalContent(breakpoint.content, breakpoint.layout, quizCtaLabel, idBase)
    : breakpoint.content.type === "benefits"
    ? renderBenefitsContent(breakpoint.content, isMobile, idBase)
    : null;
}

// Sale slide — injected as the first slide when stickyBanner is enabled in sale-config.json.
// Desktop: black bg, center layout, shimmer texture, "Know more" CTA → /buy-now
// Mobile:  black bg, split layout, chevron on right → /buy-now
const SALE_SLIDE: StickyBannerSlide = {
  id: "flash-sale",
  desktop: {
    background: "#000000",
    layout: "center",
    content: { type: "sale", href: saleConfig.components.stickyBanner.href },
    showDesktopTexture: true,
  },
  mobile: {
    background: "#000000",
    layout: "split",
    content: { type: "sale", href: saleConfig.components.stickyBanner.href },
    showDesktopTexture: false,
  },
};

export function StickyBanner({ slides }: StickyBannerProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { handleOpenModal } = useModal();
  const { ctaLabel: quizCtaLabel, quizState } = useQuizState();

  const validSlides = useMemo(() => {
    const base = slides.filter((slide) => slide.mobile || slide.desktop);
    if (isSaleEnabled("stickyBanner")) {
      return [SALE_SLIDE, ...base.filter((slide) => slide.id !== "quiz")];
    }
    return base;
  }, [slides]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = () => setIsMobile(mediaQuery.matches);
    setIsMounted(true);

    onChange();
    mediaQuery.addEventListener("change", onChange);

    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (validSlides.length <= 1) {
      setCurrentSlide(0);
      setPreviousSlide(null);
      return;
    }

    const interval = window.setInterval(() => {
      setCurrentSlide((prev) => {
        setPreviousSlide(prev);
        return (prev + 1) % validSlides.length;
      });
    }, ROTATE_MS);

    return () => window.clearInterval(interval);
  }, [validSlides.length]);

  useEffect(() => {
    if (currentSlide < validSlides.length) return;
    setCurrentSlide(0);
    setPreviousSlide(null);
  }, [currentSlide, validSlides.length]);

  // Fire impression once per session when the quiz slide first becomes active.
  const quizSlideIndex = useMemo(
    () => validSlides.findIndex((s) => s.id === "quiz"),
    [validSlides],
  );

  useEffect(() => {
    if (quizSlideIndex === -1) return;
    if (currentSlide !== quizSlideIndex) return;
    fireQuizTouchpointImpression("top_strip");
  }, [currentSlide, quizSlideIndex]);

  if (validSlides.length === 0) return null;

  const fallbackSlide = validSlides[0]!;

  // Reserve the same height on SSR + first client paint so the sticky strip
  // does not push the hero down after hydration (banner is in normal flow).
  if (!isMounted) {
    return (
      <div
        className="sticky top-0 z-50 h-12 w-full shrink-0 overflow-hidden backdrop-blur-[33px]"
        aria-hidden
      >
        <div
          className="h-full w-full"
          style={{
            background: getSlideBackground(fallbackSlide, false),
          }}
        />
      </div>
    );
  }

  const currentSlideData = validSlides[currentSlide] ?? fallbackSlide;
  const currentBreakpoint = getResolvedBreakpoint(
    currentSlideData,
    isMobile,
  );

  return (
    <div
      id="header-strip"
      data-section-type="header_strip"
      className="sticky top-0 z-50 h-12 w-full shrink-0 overflow-hidden backdrop-blur-[33px]"
    >
      <div
        className="relative h-full w-full"
      >
        {/* Static background — not animated */}
        <div
          className="absolute inset-0"
          style={{
            background: getSlideBackground(currentSlideData, isMobile),
          }}
        >
          {currentBreakpoint?.showDesktopTexture !== false ? (
            <DesktopBannerTexture />
          ) : null}
        </div>

        {/* Animated text content only */}
        {validSlides.map((slide, index) => {
          const href = getSlideHref(slide, isMobile);
          const content = renderBreakpointContent(slide, isMobile, quizCtaLabel, index);
          const animationClass = [
            "absolute inset-0 h-full w-full",
            validSlides.length > 1 && currentSlide === index
              ? "home-banner-slide-in z-20"
              : "",
            validSlides.length > 1 && previousSlide === index
              ? "home-banner-slide-out z-10"
              : "",
            validSlides.length > 1 &&
            currentSlide !== index &&
            previousSlide !== index
              ? "opacity-0"
              : "",
          ].join(" ");

          const modalId = getSlideModalId(slide, isMobile);

          return (
            <div
              key={slide.id}
              className={animationClass}
            >
              {href ? (
                <button
                  type="button"
                  id={`sticky-banner-slide-${index + 1}-cta`}
                  data-testid={`sticky-banner-slide-${index + 1}-cta`}
                  className="block h-full w-full cursor-pointer text-left"
                  onClick={() => {
                    sendEvent(
                      EventNames.TRY_INITO_CLICKED,
                      withSectionType(SectionTypes.HEADER_STRIP),
                    );
                    const sectionId = getSectionIdFromHref(href);
                    const fallbackId = sectionId
                      ? SECTION_ID_FALLBACKS[sectionId]
                      : undefined;
                    const el = sectionId
                      ? (getVisibleSectionById(sectionId) ??
                        (fallbackId ? getVisibleSectionById(fallbackId) : null))
                      : null;
                    if (el) {
                      const rect = el.getBoundingClientRect();
                      const top = Math.max(0, window.scrollY + rect.top - 116);
                      window.scrollTo({ top, behavior: "smooth" });
                    } else if (!sectionId) {
                      window.location.href = href;
                    }
                  }}
                >
                  {content}
                </button>
              ) : modalId ? (
                <button
                  type="button"
                  id={`sticky-banner-slide-${index + 1}-cta`}
                  data-testid={`sticky-banner-slide-${index + 1}-cta`}
                  className="block h-full w-full text-left cursor-pointer"
                  onClick={() => {
                    sendEvent(EventNames.QUIZ_TOUCHPOINT_CLICKED, {
                      quiz_source: QUIZ_SOURCE,
                      touchpoint_type: "top_strip",
                      scroll_depth:
                        document.documentElement.scrollHeight > window.innerHeight
                          ? Math.round(
                              (window.scrollY /
                                (document.documentElement.scrollHeight -
                                  window.innerHeight)) *
                                100,
                            )
                          : 0,
                      page_url: window.location.href,
                      user_state: quizState.status,
                    });
                    handleOpenModal(modalId, { touchpointType: "top_strip" });
                  }}
                >
                  {content}
                </button>
              ) : (
                content
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
